import React from "react";
import { TabNavigationBody } from "./TabNavigationBody";
import { TabsWithNavigationProps, TabWithContent } from "./types";
import { FlatTab } from "@/components/FlatTab";
import { useLocation } from "react-router-dom";
import { CheckCircle } from "lucide-react";
import { useProtectedNavigation } from "@/routes/useProtectedNavigation";

export type {
  TabWithContent,
  TabsWithNavigationProps,
  TabNavigationBodyProps,
} from "./types";

/**
 * TabsWithNavigation - A reusable tabs component with protected navigation
 *
 * All navigation uses RouteId-based protected routing with automatic permission checking.
 *
 * FEATURES:
 * - Protected routing with automatic permission checking
 * - Tab completion tracking
 * - Responsive layout with proper styling
 * - TypeScript support with RouteId validation
 */
export const TabsWithNavigation = ({
  title,
  routes,
  onTabChange,
  children,
  completedTabs = new Set(),
}: TabsWithNavigationProps) => {
  const { pathname } = useLocation();
  const { navToRoute } = useProtectedNavigation();

  const handleTabClick = (id: string, route: TabWithContent) => {
    const changeTab = () => {
      if (route.route) {
        navToRoute(route.route, route.params);
      }
    };

    onTabChange(id, changeTab);
  };

  const isTabCompleted = (tabId: string) => completedTabs.has(tabId);
  const getTabLabel = (route: TabWithContent) => route.handle?.name || route.id;

  const tabItems = routes.map((route, index) => {
    const isCompleted = isTabCompleted(route.id);

    return {
      label: getTabLabel(route),
      active: route.id === pathname.split("/").pop(),
      number: index + 1,
      completed: isCompleted,
      onClick: () => handleTabClick(route.id, route),
      icon: isCompleted ? (
        <CheckCircle
          className={`h-5 w-5 ${index + 1 === routes.length && "text-success-primary"}`}
        />
      ) : undefined,
    };
  });

  return (
    <div className="flex flex-col">
      {/* Header */}
      <div className="border-b px-6 pt-6">
        <h1 className="mb-4 text-2xl font-semibold text-foreground">{title}</h1>

        {/* Tabs */}
        <div className="container">
          <FlatTab items={tabItems} />
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 bg-background">
        <div className="container px-6 py-8">{children}</div>
      </div>
    </div>
  );
};

TabsWithNavigation.Body = TabNavigationBody;
